import React, { useState } from 'react';
import { Box, Button, Container, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSendContactEmailMutation } from '../api/contactApi';

interface ContactProps {
  handleClose: () => void;
}

export default function ContactForm({ handleClose }: ContactProps) {
  const { t } = useTranslation();
  const [sendContactEmail] = useSendContactEmailMutation();

  // États séparés pour les valeurs et leurs erreurs
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string>('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>('');

  // Validation
  const validateEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Handlers
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setName(v);

    if (v.trim().length === 0) {
      setNameError('Name is required');
    } else if (v.length < 3) {
      setNameError('Name must be at least 3 characters');
    } else {
      setNameError('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setEmail(v);

    if (v.trim().length === 0) {
      setEmailError('Email is required');
    } else if (!validateEmail(v)) {
      setEmailError('Invalid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // ne pas envoyer si erreur
    if (nameError || emailError) {
      alert('Form is invalid! Please check the fields...');
      return;
    }

    try {
      await sendContactEmail({ name, email }).unwrap();
      alert('Email sent successfully');
      handleClose();
    } catch {
      alert('Failed to send email');
    }

    // reset
    setName('');
    setEmail('');
  };


  const getSx = (hasError: boolean) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: '#282c34',
      // état normal
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'lightgrey',
        borderRadius: 2,
      },
      // hover
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'white',
      },
      ...(hasError && {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'red',
        },
      }),
    },
    // label : MUI gère le shrink au focus/valeur automatiquement
    '& .MuiInputLabel-root': {
      color: hasError ? 'red' : 'white',
    },
    // helper text
    '& .MuiFormHelperText-root': {
      color: 'red',
      mt: 0.5,
      mb: 0,
    },
  });

  return (
    <Container maxWidth="xs">
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 2,
          p: 3,
          // bgcolor: '#1a1b1f',
          borderRadius: 2,
        }}
      >
        <TextField
          label={t('name')}
          variant="outlined"
          fullWidth
          size="small"
          value={name}
          onChange={handleNameChange}
          error={!!nameError}
          helperText={nameError}
          slotProps={{
            input: { sx: { color: 'white', bgcolor: '#282c34', borderRadius: 8, } },
            formHelperText: { sx: { color: 'red' } },
          }}
          sx={getSx(!!nameError)}
        />

        <TextField
          label={t('email')}
          variant="outlined"
          fullWidth
          size="small"
          type="email"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          slotProps={{
            input: { sx: { color: 'white', bgcolor: '#282c34' } },
            formHelperText: { sx: { color: 'red' } },
          }}
          sx={getSx(!!emailError)}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: 20,
              color: 'white',
              borderColor: 'grey.600',
              '&:hover': { borderColor: 'white' },
              textTransform: 'none',
              px: 3,
              py: 1,
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="outlined"
            type="submit"
            sx={{
              borderRadius: 20,
              color: 'white',
              borderColor: 'cyan',
              '&:hover': { borderColor: 'white' },
              textTransform: 'none',
              px: 3,
              py: 1,
            }}
          >
            {t('send')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
